import { MaggieHttp } from '../MaggieHttp';

/**
 * Legal structure reads (FD-4399).
 *
 * Shapes verified against `src/legal-structure/dto/legal-structure-response.dto.ts` on
 * api main (#4605, merged 2026-08-14).
 *
 * These reads carry only AuthGuard on the API side; the LaunchDarkly gate
 * (`legalStructureEnabled`) covers the mutation controllers. Callers still handle a
 * 404 so the UI degrades if an environment is behind.
 */

/** Matches `NodeType` in legal-structure/node-ref.ts. */
export type LegalStructureNodeType = 'fund' | 'company' | 'family';

/** Matches `RelationshipType` in legal-structure/node-ref.ts. */
export type LegalStructureRelationshipType =
  | 'EQUITY'
  | 'LOAN'
  | 'GP_COMMITMENT'
  | 'CARRY'
  | 'MANAGEMENT'
  | 'VOTING'
  | 'LP_COMMITMENT'
  /**
   * Fund-family membership (FD-4480).
   *
   * Carried by the API and read by the FE to group funds under their family, but never
   * drawn: the chart has no membership line, so `KNOWN_RELATIONSHIP_TYPES` filters it out
   * of the edges the canvas renders. It expresses grouping, not a connection.
   */
  | 'MEMBERSHIP';

export interface ILegalStructureNodeRef {
  nodeType: LegalStructureNodeType;
  nodeId: number;
}

/** Display kind, matching `DisplayEntityKind` in legal-structure/node-ref.ts. */
export type LegalStructureEntityKind =
  'FAMILY' | 'FUND' | 'HOLDING_CO' | 'BLOCKER' | 'SPV' | 'GP_ENTITY' | 'MANAGEMENT_CO' | 'PORTFOLIO_COMPANY';

/**
 * `GraphNodeResponseDto` — deliberately leaner than the register's row.
 *
 * The graph read carries topology and identity only: no legalForm, jurisdiction,
 * taxId or the edge-derived `role`. Those live on `GET /legalStructure/entities`
 * (`EntityRegisterRowDto`), which is what FD-4472 consumes.
 */
export interface ILegalStructureGraphNode {
  ref: ILegalStructureNodeRef;
  name: string;
  kind: LegalStructureEntityKind;
  ledgerBacked: boolean;
  fundId?: number;
  companyId?: number;
  fundKind?: 'fund' | 'intermediary';
}

export interface ILegalStructureEdge {
  id: number;
  parentType: LegalStructureNodeType;
  parentId: number;
  childType: LegalStructureNodeType;
  childId: number;
  relationshipType: LegalStructureRelationshipType | string;
  ownershipPercentage: number | null;
  /** VOTING edges only; null everywhere else. Optional: older API builds omit it. 0-100. */
  votingPercentage?: number | null;
  principalAmount: number | null;
  interestRate: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  source: string;
  /**
   * Always 'active' on this read: the BE graph hard-filters to active edges
   * (entity-relationship.service `findEdges` — proposed/rejected rows never reach
   * the chart), so the canvas deliberately does not branch on it.
   */
  status?: string;
  /** Edge-detail content — surfaced by the relationship detail panel (FD-4469), not the canvas. */
  notes?: string | null;
}

/** Matches `EntityRole` in legal-structure/node-ref.ts. Fund roles are edge-derived. */
export type LegalStructureEntityRole =
  | 'MAIN_FUND'
  | 'FEEDER_PARALLEL'
  | 'CARRY_CO_INVEST'
  | 'HOLDING_CO'
  | 'BLOCKER'
  | 'SPV'
  | 'GP_ENTITY'
  | 'MANAGEMENT_CO'
  | 'FAMILY';

/**
 * `EntityRegisterRowDto` — the full legal record, which the graph payload does not
 * carry. Backs the registry table (FD-4472), and the chart joins it for the
 * edge-derived `role` plus the type and jurisdiction chips.
 */
export interface ILegalStructureEntityRow {
  ref: ILegalStructureNodeRef;
  name: string;
  legalName: string | null;
  kind: LegalStructureEntityKind;
  role: LegalStructureEntityRole;
  fundKind?: 'fund' | 'intermediary';
  status: string;
  ledgerBacked: boolean;
  fundId?: number;
  legalForm: string | null;
  jurisdiction: string | null;
  formationDate: string | null;
  dissolvedDate: string | null;
  taxId: string | null;
  registrationNumber: string | null;
  taxClassification: string | null;
  taxResponsibleParty: string | null;
  registeredAgent: string | null;
  registeredAddress: string | null;
  mailingAddress: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  /**
   * Per-entity economics. Not on `EntityRegisterRowDto` yet — the register is a legal
   * record, and `/rollup` keys cost and fmv by (root, companyId) rather than by
   * entity. Declared optional so the registry starts showing them the moment BE adds
   * them, with no FE change.
   */
  cost?: number | null;
  fmv?: number | null;
  /**
   * Fields on this entity with a re-extracted value awaiting a decision (FD-4473).
   *
   * Optional because an API that predates it omits it — the row then shows no review
   * badge, rather than the table breaking.
   */
  pendingReviewCount?: number;

  /**
   * Instruments hosted at this entity (FD-4482), joined onto the register by
   * `structure.controller.ts` so the chart can chip every card from one read rather than
   * one call per node.
   *
   * `hostedDebt` is null rather than 0 when the entity hosts no debt — zero debt is no
   * debt, and a 0 here would light the red chip on every equity-only vehicle.
   */
  hostedDebt?: number | null;
  hostsPec?: boolean;
  hostedInstrumentCount?: number;
}

/**
 * Every fund, legal entity and family with edge-derived roles and completeness fields.
 *
 * Unrooted, unlike `/graph` — this is the whole register. Internal-only for now (not
 * promoted to the external API document).
 */
export async function fetchLegalStructureEntities(): Promise<ILegalStructureEntityRow[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureEntityRow[]>('/legalStructure/entities');

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

export interface ILegalStructureGraphResponse {
  root: ILegalStructureNodeRef;
  asOf: string;
  nodes: ILegalStructureGraphNode[];
  edges: ILegalStructureEdge[];
}

export interface IGraphQueryParams {
  rootType: LegalStructureNodeType;
  rootId: number;
  /** ISO date. Always pass it — the endpoint defaults to today(), not a period end. */
  asOf?: string;
  /** 'up' walks to the root's holder closure; 'both' adds its holdings — the entity view. Default 'down'. */
  direction?: 'down' | 'up' | 'both';
}

/**
 * Returns null when the endpoint is not deployed, so the caller can distinguish
 * "no structure data" from "this API does not exist yet" without try/catch at
 * every call site.
 */
export async function fetchLegalStructureGraph(
  params: IGraphQueryParams
): Promise<ILegalStructureGraphResponse | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureGraphResponse>('/legalStructure/graph', {
      params,
    });

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

export type LegalStructureRollupSource = 'ledger' | 'allocated' | 'estimated';

/**
 * Why a path did or did not contribute to the holder's effective ownership.
 *
 * `resolveCompany` (api rollup-math.ts) keeps only the paths whose source tier is the
 * most precise available for that (root, company) pair: those are `used`, better-sourced
 * siblings demote the rest to `droppedLowerTier`, and paths with no computable value are
 * `uncomputable`. Only `used` paths sum to `effectiveOwnership`.
 */
export type LegalStructureRollupPathStatus = 'used' | 'droppedLowerTier' | 'uncomputable';

/**
 * One ownership route from an ultimate vehicle down to the subject company.
 *
 * The shape has two traps, both verified against `RollupPathResponseDto` and
 * `findHolderPaths` / `evaluatePath` in api `src/legal-structure/rollup-math.ts`:
 *
 *  1. `nodes` runs top-of-chain → direct holder of record and does **not** include the
 *     subject company. The traversal stops at the first vehicle whose paired fund books
 *     a ledger position in the subject, so the last element is that holder.
 *  2. `edgeOwnerships.length === nodes.length - 1`; `edgeOwnerships[i]` is the EQUITY
 *     edge from `nodes[i]` to `nodes[i + 1]`. There is **no entry for the final hop**
 *     (holder → subject) because that link is a ledger position rather than an edge. Its
 *     percentage is `FundCompany.ownerShipPercentage`, which `GET /metrics` serves from
 *     the same snapshot the roll-up reads — join on the holder's paired `fundId` rather
 *     than dividing it back out of `pathOwnership`. Note this is *not* the
 *     `ownershipPercentage` on a holder → company edge: the roll-up discards that.
 *
 * All percentages are 0–100, never fractions (`evaluatePath` does `edgePct / 100`).
 */
export interface ILegalStructureRollupPath {
  nodes: ILegalStructureNodeRef[];
  edgeOwnerships: (number | null)[];
  /** Compounded percentage of the subject this path confers. Null when any hop is null. */
  pathOwnership: number | null;
  status: LegalStructureRollupPathStatus;
}

/** An ultimate vehicle: a node with no EQUITY parent, excluding the subject itself. */
export interface ILegalStructureRolldownHolder {
  root: ILegalStructureNodeRef;
  /** Percentage 0–100. Use verbatim; never re-sum the paths (spec AC 4). */
  effectiveOwnership: number | null;
  source: LegalStructureRollupSource;
  /** Ledger-derived economic proxy (share of positive booked cost); optional on older APIs. */
  costBasisOwnership?: number | null;
  paths: ILegalStructureRollupPath[];
}

/** Note: carries neither `asOf` nor the subject's name — the caller owns both. */
export interface ILegalStructureRolldownResponse {
  companyId: number;
  holders: ILegalStructureRolldownHolder[];
}

export interface IRolldownQueryParams {
  companyId: number;
  /** ISO date. Always pass it — the endpoint defaults to today(), not a period end. */
  asOf?: string;
}

/**
 * Every root holding a stake in one company, with its rolled-up effective ownership.
 *
 * Returns null when the endpoint is not deployed, matching the other reads here.
 */
export async function fetchLegalStructureRolldown(
  params: IRolldownQueryParams
): Promise<ILegalStructureRolldownResponse | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureRolldownResponse>(
      '/legalStructure/rolldown',
      { params }
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * Ledger entity proposals (FD-4477).
 *
 * The ledger pipeline parks any GL row whose `LEGAL_ENTITY` string it cannot resolve and
 * files a proposed company record for the string. Shapes verified against
 * `src/legal-structure/proposal.controller.ts` and `proposal.service.ts` on api main.
 *
 * Unlike the reads above, `PUT` carries `@UserAccess({ permissions: [INTERNAL] })` **and**
 * the `legalStructureEnabled` LaunchDarkly gate, which fails closed with a 403.
 */

/** `PROPOSAL_STATUSES` in legal-structure.dto.ts. There is no `accepted` — accepting
 *  promotes the row to `entityStatus: 'active'`, which drops it out of both lists. */
export type LegalStructureProposalStatus = 'proposed' | 'rejected';

/**
 * `ENTITY_KINDS` in legal-structure.dto.ts — the kinds a proposal may be accepted as.
 * Narrower than `LegalStructureEntityKind`: a proposal cannot become a FUND or a
 * PORTFOLIO_COMPANY, so those are absent and the accept DTO rejects them.
 */
export type LegalStructureProposalEntityKind =
  'HOLDING_CO' | 'BLOCKER' | 'SPV' | 'GP_ENTITY' | 'MANAGEMENT_CO' | 'FAMILY';

export const LEGAL_STRUCTURE_PROPOSAL_ENTITY_KINDS: LegalStructureProposalEntityKind[] = [
  'HOLDING_CO',
  'BLOCKER',
  'SPV',
  'GP_ENTITY',
  'MANAGEMENT_CO',
  'FAMILY',
];

/** One raw ledger string that resolved to this proposal. */
export interface ILegalStructureProposalAlias {
  /** The `LEGAL_ENTITY` value as it appears in the ledger. */
  externalName: string;
  /** Which feed it came from. */
  source: string;
}

/** `ProposalSummary` in proposal.service.ts. */
export interface ILegalStructureProposal {
  ref: ILegalStructureNodeRef;
  name: string;
  entityKind: string;
  aliases: ILegalStructureProposalAlias[];
  /**
   * Unprocessed `generalLedger` rows matching any of this proposal's raw strings.
   * Counted case-insensitively across every alias, so it is the number of bookings
   * currently excluded from roll-ups because of this one proposal.
   */
  parkedRowCount: number;
}

/**
 * Returns null when the endpoint is not deployed, matching the reads above.
 *
 * `status` defaults to `proposed` server-side; passed explicitly so the query key and
 * the request never disagree about which list is cached.
 */
export async function fetchLegalStructureProposals(
  status: LegalStructureProposalStatus
): Promise<ILegalStructureProposal[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureProposal[]>('/legalStructure/proposals', {
      params: { status },
    });

    return response.data;
  } catch (error: unknown) {
    const httpStatus = (error as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 404 || httpStatus === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * `ProposalDecisionDTO`. Three outcomes behind two actions:
 *
 * - `accept` alone promotes the proposal to a live entity and re-queues its parked rows.
 * - `accept` with `mapping` **merges** instead: the proposal's aliases move to the target
 *   node and the proposal record is deleted.
 * - `reject` is terminal — the endpoint 409s on any proposal that is no longer `proposed`.
 */
export interface ILegalStructureProposalDecision {
  action: 'accept' | 'reject';
  /** Present only for a merge. The existing node the aliases move to. */
  mapping?: ILegalStructureNodeRef;
  /**
   * Fund to pair the accepted entity with. **Omitting it does not fail** — the parked
   * rows are instead stamped "awaiting fund pairing" and stay excluded from roll-ups.
   */
  pairFundId?: number;
  /** Ignored unless the action is a plain accept. */
  entityKind?: LegalStructureProposalEntityKind;
  /** Accept as structure-only: joins the structure; books stay unloaded. Mutually exclusive with pairFundId. */
  structureOnly?: boolean;
}

/**
 * Decides one proposal. Errors are deliberately **not** swallowed: every failure mode
 * here is one the reviewer has to see.
 *
 * - **409** on a merge whose proposal already has relationships. `mapToExisting` runs
 *   `deleteEntity` first precisely so this leaves the alias rows untouched — the merge
 *   did not half-happen.
 * - **409** on any proposal whose status is no longer `proposed` (someone else decided it).
 * - **403** without `fd.internal`, or when `legalStructureEnabled` is off for the tenant.
 */
export async function decideLegalStructureProposal(
  proposalId: number,
  decision: ILegalStructureProposalDecision
): Promise<void> {
  await MaggieHttp.maggie.put(`/legalStructure/proposals/${proposalId}`, decision);
}

/**
 * `RollupRowResponseDto` — one row per portfolio company, not per entity.
 *
 * Keyed per company, so these rows say nothing about the vehicles a position is held
 * through. Values for those come from `entities` below, computed server-side — the FE
 * still derives nothing, which is what keeps chart badges and registry columns agreeing
 * (AC 7).
 */
export interface ILegalStructureRollupRow {
  companyId: number;
  effectiveOwnership: number | null;
  cost: number | null;
  fmv: number | null;
  source: LegalStructureRollupSource;
  paths: ILegalStructureRollupPath[];
}

/**
 * `EntityValueResponseDto` — cost and FMV at a node that is not a portfolio company.
 *
 * Added to `/rollup` by the legal-structure P6 work. `resolveEntityValues` BE-side
 * accumulates a value for each fund, for every intermediate node a booked position flows
 * through (`viaNodeIds`), and for companies paired to an intermediary fund. **Portfolio
 * companies are deliberately absent** — those are the look-through `rows` above, which
 * carry effective ownership as well.
 *
 * `incomplete` is computed server-side here, unlike `rows`, where the FE reads it off the
 * path statuses. `source` is always `'ledger'` today: attribution comes from the
 * `legalEntityId` stamped on transactions rather than from any apportionment.
 */
export interface ILegalStructureEntityValue {
  ref: ILegalStructureNodeRef;
  cost: number | null;
  fmv: number | null;
  source: LegalStructureRollupSource;
  incomplete: boolean;
}

/**
 * Which of the two derivations produced an edge's value.
 *
 * `booked` is the position the parent actually books in the child, and the API only ever
 * produces it when the child is a company — so an edge into a holdco, blocker or SPV is
 * `apportioned` by definition rather than for want of data.
 *
 * `apportioned` scales the child's own holdings by the allocation % where one exists, and
 * by the edge % otherwise.
 */
export type LegalStructureEdgeValueBasis = 'booked' | 'apportioned';

/**
 * `EdgeValueResponseDto` — one equity edge's cost and FMV.
 *
 * **Never sum these.** Two equity edges between the same pair (separate share classes,
 * say) each report the *whole* booked position, because nothing in the data splits it.
 * Totalling edge values into a node inflates that node.
 *
 * Only EQUITY edges appear: `resolveEdgeValues` skips every other relationship type,
 * because a loan carries `principalAmount` — a face value, not a booked position.
 */
export interface ILegalStructureEdgeValue {
  edgeId: number;
  cost: number | null;
  fmv: number | null;
  basis: LegalStructureEdgeValueBasis;
}

/**
 * `FocusedRollupRowResponseDto` — one holder's look-through into the focused company.
 *
 * A `RollupRow` rooted at `ref` instead of at the request's root: what *this* holder has
 * in the focused company, which is the number a fund badge should carry while the chart
 * is narrowed to one company. `ILegalStructureEntityValue` for the same holder is its
 * whole book, and the two are deliberately different answers.
 */
export interface ILegalStructureFocusedRow extends ILegalStructureRollupRow {
  ref: ILegalStructureNodeRef;
}

export interface ILegalStructureRollupResponse {
  root: ILegalStructureNodeRef;
  asOf: string;
  rows: ILegalStructureRollupRow[];
  /**
   * Optional because an environment behind on the API simply omits it — the chart then
   * badges companies only, exactly as it did before entity-tier values existed.
   */
  entities?: ILegalStructureEntityValue[];
  /**
   * Optional for the same reason as `entities`, and **sparse even when present**: the API
   * returns only the edges it could derive a value for, so a missing edge id means neither
   * basis applied. That is a different thing from an edge whose `cost` is null.
   */
  edges?: ILegalStructureEdgeValue[];
  /** Present only when `focusCompanyId` was asked for. */
  focus?: ILegalStructureNodeRef;
  /**
   * Every holder above the focused company, client-wide, with its look-through into it.
   * Present only when `focusCompanyId` was asked for; sparse, since a holder whose
   * look-through cannot be computed has no row.
   */
  focused?: ILegalStructureFocusedRow[];
}

export interface IRollupQueryParams {
  rootType: LegalStructureNodeType;
  rootId: number;
  /** ISO date. Always pass it — the endpoint defaults to today(), not a period end. */
  asOf?: string;
  /** The company the chart is narrowed to; asks for the `focused` series. */
  focusCompanyId?: number;
}

/**
 * Cost, FMV and effective ownership per company beneath a root.
 *
 * Returns null when the endpoint is not deployed, matching its two siblings above, so
 * the chart can tell "no roll-up data" from "this API does not exist yet".
 */
export async function fetchLegalStructureRollup(
  params: IRollupQueryParams
): Promise<ILegalStructureRollupResponse | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureRollupResponse>('/legalStructure/rollup', {
      params,
    });

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/* ---------------------------------------------------------------------------
 * Writes (FD-4471)
 *
 * Every mutation below is `UserAccess({ permissions: [UserPermission.INTERNAL] })` and
 * `assertEnabled()` BE-side: fail-closed on `legalStructureEnabled`, 403 otherwise. The
 * FE mirrors both in `useEntityManagementCanEdit` so an affordance is never offered for a
 * request that is guaranteed to be rejected.
 *
 * Errors are deliberately not caught here — unlike the reads above, which map 404/501 to
 * null so a behind-the-times environment degrades quietly. A failed write must reach the
 * caller: the BE's rejection text is the content of the inline error the reviewer reads.
 * ------------------------------------------------------------------------- */

/**
 * The kinds `POST /legalEntity` will create.
 *
 * The same `ENTITY_KINDS` set the proposal flow above accepts — one BE enum, so this
 * aliases that constant rather than restating the six members. Narrower than the display
 * kinds: FUND and PORTFOLIO_COMPANY are real display kinds but come from the funds and
 * companies tables, and are never created through this endpoint.
 */
export const LEGAL_STRUCTURE_CREATABLE_KINDS = LEGAL_STRUCTURE_PROPOSAL_ENTITY_KINDS;

export type LegalStructureCreatableKind = LegalStructureProposalEntityKind;

/** `LEGAL_FORMS` in legal-structure/legal-structure.dto.ts. */
export const LEGAL_FORMS = ['LP', 'LLC', 'CORP', 'LTD', 'OTHER'] as const;

export type LegalForm = (typeof LEGAL_FORMS)[number];

/**
 * `LegalEntityDTO`, narrowed to what the Add-entity form collects.
 *
 * Covers what `LegalEntityDTO` accepts on create, minus three deliberate omissions:
 *
 * - `committedCapital` and `currencyCode` are economic values, and no form in this module
 *   accepts one — cost and FMV are roll-ups of booked positions.
 * - `registeredAgent` is maintained in the D&O slate rather than typed here, so collecting
 *   it would give two sources for one fact.
 */
export interface ICreateLegalEntityInput {
  name: string;
  entityKind: LegalStructureCreatableKind;
  legalName?: string;
  legalForm?: LegalForm;
  jurisdiction?: string;
  /** ISO date. */
  formationDate?: string;
  /** ISO date. The record's "closed date". */
  dissolvedDate?: string;
  registrationNumber?: string;
  /** EIN. */
  taxId?: string;
  taxClassification?: string;
  taxResponsibleParty?: string;
  registeredAddress?: string;
  mailingAddress?: string;
  notes?: string;
}

/** `createEntity` returns `{ ref, ...entity }`; the ref is what the caller needs. */
export interface ICreateLegalEntityResponse {
  ref: ILegalStructureNodeRef;
}

export async function createLegalEntity(input: ICreateLegalEntityInput): Promise<ICreateLegalEntityResponse> {
  const response = await MaggieHttp.maggie.post<ICreateLegalEntityInput, ICreateLegalEntityResponse>(
    '/legalEntity',
    input
  );

  return response.data;
}

/**
 * The updatable record, which is wider than the create form's input.
 *
 * `LegalEntityUpdateDTO` is `PartialType(LegalEntityDTO)`, so every column below is
 * writable — but `ICreateLegalEntityInput` deliberately exposes only what + Add entity
 * asks for. Widening that one would put tax ids and addresses on the create modal;
 * this type keeps the two forms' surfaces separate while writing to the same endpoint.
 *
 * `entityKind` is absent on purpose: changing an entity's kind after creation reclassifies
 * it across the chart, the registry's role derivation and the lens grammar, which is a
 * different operation from correcting its legal record.
 */
export interface ILegalEntityRecordPatch {
  name?: string;
  legalName?: string | null;
  legalForm?: string | null;
  jurisdiction?: string | null;
  formationDate?: string | null;
  dissolvedDate?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
  taxClassification?: string | null;
  taxResponsibleParty?: string | null;
  registeredAgent?: string | null;
  registeredAddress?: string | null;
  mailingAddress?: string | null;
  notes?: string | null;
}

export async function updateLegalEntity(
  ref: ILegalStructureNodeRef,
  input: ILegalEntityRecordPatch
): Promise<void> {
  await MaggieHttp.maggie.put(`/legalEntity/${ref.nodeType}/${ref.nodeId}`, input);
}

/**
 * `EntityRelationshipDTO`. `ownershipPercentage` is 0-100 (`@Min(0) @Max(100)`), matching
 * the read shape rather than a 0-1 fraction.
 *
 * `principalAmount` and `interestRate` are on the DTO and omitted here on purpose: a
 * loan's face value is an economic term, and FD-4471 accepts no economic value.
 * Intercompany loans are created by ingestion, not by hand.
 */
export interface ICreateEntityRelationshipInput {
  parent: ILegalStructureNodeRef;
  child: ILegalStructureNodeRef;
  relationshipType: LegalStructureRelationshipType;
  ownershipPercentage?: number;
  /** VOTING edges only; 0-100. The API 422s it on any other type. */
  votingPercentage?: number;
  /** ISO date. */
  effectiveFrom: string;
  effectiveTo?: string;
  notes?: string;
}

export async function createEntityRelationship(
  input: ICreateEntityRelationshipInput
): Promise<ILegalStructureEdge> {
  const response = await MaggieHttp.maggie.post<ICreateEntityRelationshipInput, ILegalStructureEdge>(
    '/entityRelationship',
    input
  );

  return response.data;
}

/**
 * Ends an edge. This is the only thing `PUT /entityRelationship/:id` accepts — it 422s on
 * any other field with "to change ownership or terms, close this edge and create a new
 * one", because edge history is append-only.
 *
 * `DELETE /entityRelationship/:id` exists and is intentionally not wrapped: the UI never
 * hard-deletes a relationship, it ends one.
 */
export async function closeEntityRelationship(id: number, effectiveTo: string): Promise<void> {
  await MaggieHttp.maggie.put(`/entityRelationship/${id}`, { effectiveTo });
}

/**
 * `SupersedeEdgeDTO`. One transaction closing an edge and opening its successor on the
 * same date, replacing the close-then-create pair whose halves could fail apart.
 *
 * Only EQUITY and VOTING edges can be superseded, and each carries only its own
 * percentage — the API 422s the other one, and refuses an edge holding loan terms rather
 * than rebuilding it without them.
 */
export interface ISupersedeEdgeInput {
  /** Closes the old edge and starts the successor: one date, adjacent halves. */
  effectiveFrom: string;
  ownershipPercentage?: number;
  votingPercentage?: number;
  /** The old edge's notes carry over when omitted. */
  notes?: string;
}

export async function supersedeEntityRelationship(
  id: number,
  input: ISupersedeEdgeInput
): Promise<ILegalStructureEdge> {
  const response = await MaggieHttp.maggie.put<ISupersedeEdgeInput, ILegalStructureEdge>(
    `/entityRelationship/${id}/supersede`,
    input
  );

  return response.data;
}

/**
 * `AllocationResponseDto` — how one edge's value is apportioned between targets.
 *
 * `derivation` separates a hand-entered slice from one the ingestion pipeline derived
 * (`derived` rows carry a `derivationBatchId` BE-side). The panel shows the difference
 * rather than letting a user overwrite a derived row believing it was theirs.
 */
export interface ILegalStructureAllocation {
  id: number;
  relationshipId: number;
  targetType: LegalStructureNodeType;
  targetId: number;
  allocationPercentage: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  derivation: string;
}

/** Read-side, so it maps a missing endpoint to null like the other reads. */
export async function fetchEdgeAllocations(edgeId: number): Promise<ILegalStructureAllocation[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureAllocation[]>(
      `/entityRelationship/${edgeId}/allocations`
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/** `AllocationDTO`. Percentages are 0-100 and sum-checked BE-side per overlapping window. */
export interface IEdgeAllocationInput {
  targetRef: ILegalStructureNodeRef;
  allocationPercentage: number;
  /** ISO date. */
  effectiveFrom: string;
  effectiveTo?: string;
}

/**
 * `POST` and `PUT /entityRelationship/:id/allocations` are the same handler (both call
 * `allocationService.upsert`), so only one is wrapped.
 */
export async function upsertEdgeAllocation(edgeId: number, input: IEdgeAllocationInput): Promise<void> {
  await MaggieHttp.maggie.post(`/entityRelationship/${edgeId}/allocations`, input);
}

/* ---------------------------------------------------------------------------
 * Entity records, provenance and audit (FD-4473)
 * ------------------------------------------------------------------------- */

/**
 * `FieldProvenanceState` on the API — where one field's current value came from.
 *
 * `extracted` means a deal doc produced it and nobody has affirmed it. `confirmed` means
 * a person stood behind it. `manual` means a person typed it. The distinction between the
 * last two matters for the review flow: only a *confirmed* value is protected from being
 * overwritten by a later extraction.
 */
export type LegalStructureFieldProvenanceState = 'extracted' | 'confirmed' | 'manual';

/**
 * `legalEntityFieldProvenance`, as served beside the record.
 *
 * A non-null `proposedValue` is exactly "this field is in review": a re-extraction
 * disagreed with a confirmed value and was parked rather than applied.
 */
export interface ILegalStructureFieldProvenance {
  state: LegalStructureFieldProvenanceState;
  sourceDocId: number | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  proposedValue: unknown;
  proposedAt: string | null;
  proposedSourceDocId: number | null;
}

/**
 * One entity's editable record plus its provenance.
 *
 * Provenance arrives with the record rather than from a second call: the form renders a
 * chip beside every field, so fetching it separately would mean a round trip per record
 * opened and a window where fields render without it.
 *
 * The map is keyed by field name and is **sparse** — a field nobody has touched through a
 * provenance-aware path simply has no entry, which is not the same as `manual`.
 */
export interface ILegalStructureEntityRecord {
  ref: ILegalStructureNodeRef;
  name: string;
  legalName?: string | null;
  entityKind?: string;
  legalForm?: string | null;
  jurisdiction?: string | null;
  formationDate?: string | null;
  dissolvedDate?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
  taxClassification?: string | null;
  taxResponsibleParty?: string | null;
  registeredAgent?: string | null;
  registeredAddress?: string | null;
  mailingAddress?: string | null;
  /** Add entity's "Comments" box. */
  notes?: string | null;
  /** A company or family row's own stamps; a fund's record carries neither. */
  createdBy?: string | null;
  createdAt?: string | null;
  provenance?: Record<string, ILegalStructureFieldProvenance>;
}

export async function fetchLegalEntityRecord(
  ref: ILegalStructureNodeRef
): Promise<ILegalStructureEntityRecord | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureEntityRecord>(
      `/legalEntity/${ref.nodeType}/${ref.nodeId}`
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/** `legalStructureAudit` entries, newest first. Optionally narrowed to one field. */
export interface ILegalStructureAuditEntry {
  id: number;
  action: string;
  fieldName: string | null;
  oldValue: unknown;
  newValue: unknown;
  changedBy: string | null;
  createdAt: string;
}

/**
 * Field-level history for an entity.
 *
 * **Returns an empty list for a fund**, and that is the API's answer rather than a
 * failure: funds are audited through `fundAudit`, a different table, so the endpoint
 * short-circuits. The caller has to say so instead of rendering an empty history that
 * reads as "nothing ever changed".
 */
export async function fetchLegalEntityAudit(
  ref: ILegalStructureNodeRef,
  fieldName?: string
): Promise<ILegalStructureAuditEntry[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureAuditEntry[]>(
      `/legalEntity/${ref.nodeType}/${ref.nodeId}/audit`,
      { params: fieldName ? { fieldName } : undefined }
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/** Promotes an extracted value to human-confirmed. */
export async function confirmLegalEntityField(ref: ILegalStructureNodeRef, fieldName: string): Promise<void> {
  await MaggieHttp.maggie.post(`/legalEntity/${ref.nodeType}/${ref.nodeId}/fields/${fieldName}/confirm`, {});
}

export type LegalStructureFieldReviewAction = 'accept' | 'keep';

/**
 * Answers a field review.
 *
 * `accept` writes the proposed value to the record server-side and re-confirms it; `keep`
 * discards the proposal and stands by what is there. Either way the field stops being in
 * review, so the caller refetches rather than patching state locally.
 */
export async function reviewLegalEntityField(
  ref: ILegalStructureNodeRef,
  fieldName: string,
  action: LegalStructureFieldReviewAction
): Promise<void> {
  await MaggieHttp.maggie.post(`/legalEntity/${ref.nodeType}/${ref.nodeId}/fields/${fieldName}/review`, {
    action,
  });
}

/**
 * The composed multi-entity capital stack (FD-4482).
 *
 * `GET /legalEntity/company/:id/capitalStructure` — every instrument on the company's cap
 * table, reported at the entity where it legally sits. Shapes verified against
 * `src/legal-structure/capital-structure.service.ts` on api main.
 *
 * A read, so it carries `@UserAccess()` with no `fd.internal` — unlike the legal-structure
 * *writes*, which are internal-only (FD-4702). Nothing here needs the edit gate.
 */

/** `HostNodeType` — narrower than `LegalStructureNodeType`: a family cannot hold an instrument. */
export type CapitalStructureHostNodeType = 'fund' | 'company';

export interface ICapitalStructureHost {
  nodeType: CapitalStructureHostNodeType;
  nodeId: number;
  name: string;
  kind: LegalStructureEntityKind | null;
  /** This host's effective ownership of the subject company, 0-100. */
  effectiveOwnership: number | null;
  /** Null when ownership is unknown — distinct from a known non-controlling stake. */
  isControl: boolean | null;
  /** False when the attachment points at an entity outside the company's stack. */
  inStack: boolean;
}

/**
 * The ratchet verdict, decided BE-side.
 *
 * `isInTheMoney: null` means the terms or the marks are missing — "we cannot tell", which
 * is a different statement from "it has not struck" and the chip has to be able to make it.
 */
export interface ICapitalStructureRatchet {
  thresholdMoic: number | null;
  baseSharePercentage: number | null;
  sharePercentage: number | null;
  moicAtCurrentMarks: number | null;
  isInTheMoney: boolean | null;
  effectiveSharePercentage: number | null;
}

export interface ICapitalStructureLayer {
  investmentId: number;
  name: string | null;
  investmentType: string | null;
  securityType: string | null;
  shareClassType: string | null;

  host: { nodeType: CapitalStructureHostNodeType; nodeId: number };
  /** True when the host came from the instrument's own ref rather than the cap-table fallback. */
  isAttached: boolean;

  seniority: number | null;
  pariPassu: boolean | null;
  /** 1-based; shared by pari-passu layers at the same seniority. */
  rankGroup: number;
  /** Whether the **host entity** controls the company — not a property of the instrument. */
  hostIsControl: boolean | null;

  principal: number | null;
  cashRaised: number | null;
  interestRate: number | null;
  interestType: string | null;
  compoundingPeriod: string | null;
  /** 0-1 fraction paid in kind. Null is unrecorded, never "all cash". */
  pikPortion: number | null;
  maturityDate: string | null;

  discountRate: number | null;
  valuationCap: number | null;
  conversionPrice: number | null;

  liquidationMultiplier: number | null;
  ownershipPercentage: number | null;
  fullyDilutedShares: number | null;
  outstandingShares: number | null;

  /** Computed to the as-of anchor on every read, never stored. */
  accruedInterest: number | null;
  accruedValue: number | null;
  accrualYears: number | null;

  ratchet: ICapitalStructureRatchet | null;
}

export interface ICapitalStructureResponse {
  companyId: number;
  asOf: string;
  /** Null when the company has no primary cap table — a different answer from "no instruments". */
  captableId: number | null;
  marks: { cost: number | null; fmv: number | null; moic: number | null };
  hosts: ICapitalStructureHost[];
  layers: ICapitalStructureLayer[];
}

export interface ICapitalStructureQueryParams {
  /** Plain calendar date. The stack is composed, and PEC strips accrue, to this anchor. */
  asOfDate?: string;
}

export async function fetchCapitalStructure(
  companyId: number,
  params: ICapitalStructureQueryParams
): Promise<ICapitalStructureResponse | null> {
  try {
    const response = await MaggieHttp.maggie.get<ICapitalStructureResponse>(
      `/legalEntity/company/${companyId}/capitalStructure`,
      { params }
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // Matching the other legal-structure reads: an environment without the endpoint reports
    // absent rather than failing the panel around it.
    // 403 belongs here too. Both instrument reads carry CAPTABLE_VIEW and a restricted-user
    // company check, so a refusal is an ordinary outcome for a caller without cap-table
    // access — rendering it as an error would tell the reader something had broken.
    if (status === 403 || status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * The deal-level exit waterfall (FD-4740, rendered by FD-4483).
 *
 * `GET /legalEntity/company/:id/exitWaterfall` — runs the shared fund waterfall engine over
 * this company's composed stack as a second configuration, so a deal cascade and a fund
 * cascade cannot drift into two different answers.
 *
 * **Every figure here is the engine's.** The section renders the bridge and the cascade and
 * computes neither; that boundary is the acceptance criterion, not a preference. Same access
 * model as the stack it runs on — `CAPTABLE_VIEW` plus the restricted-user company check.
 */

/** The deal classes a tier can pay into. Ordered as the cascade settles them. */
export type ExitWaterfallClassKey = 'debt' | 'pec' | 'sponsor' | 'management';

export interface IExitWaterfallTier {
  orderNumber: number;
  tierName: string;
  /** The composed stack's rank group this tier pays. Null for the equity residual. */
  rankGroup: number | null;
  classKey: ExitWaterfallClassKey;
  /** What the tier is owed. Null for equity, which is owed nothing and takes the residual. */
  claim: number | null;
  paid: number | null;
  /** Absent for equity rather than zero: zero would read as a claim met in full. */
  shortfall: number | null;
}

export interface IExitWaterfallResponse {
  companyId: number;
  asOf: string;
  /** Echoed back with the defaults applied, so the inputs on screen are the ones that ran. */
  inputs: {
    exitEv?: number | null;
    exitYear?: number | null;
    priorRealized?: number | null;
    interimIncome?: number | null;
  };
  message: string | null;
  tiers: IExitWaterfallTier[];
  totalClaims: number;
  totalDebt: number;
  /**
   * What the company is worth today on the cascade's own terms, and the exit an
   * unparameterised run uses. Null when there is no mark to anchor against.
   */
  currentEv: number | null;
  hasShortfall: boolean;
  sponsorProceeds: number;
  managementProceeds: number;
  /** Includes prior realized; excludes interim income. */
  exitMoic: number | null;
  /** A separate line, never folded into proceeds. */
  interimIncome: number | null;
  bridge: {
    modelValue: number;
    glFmv: number | null;
    /** Model value minus the GL mark, exposed whatever its size — it is a tripwire. */
    delta: number | null;
    deltaPercentage: number | null;
  };
}

export interface IExitWaterfallQueryParams {
  asOfDate?: string;
  /** Omitted runs at `currentEv`, which is what the bridge opens on. */
  exitEv?: number;
  exitYear?: number;
  priorRealized?: number;
  interimIncome?: number;
}

export async function fetchExitWaterfall(
  companyId: number,
  params: IExitWaterfallQueryParams
): Promise<IExitWaterfallResponse | null> {
  try {
    const response = await MaggieHttp.maggie.get<IExitWaterfallResponse>(
      `/legalEntity/company/${companyId}/exitWaterfall`,
      { params }
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // Absent rather than broken, on the same three statuses as the stack read this runs on:
    // the section sits beside it and the two should not disagree about whether the company
    // has cap-table data the caller may see.
    if (status === 403 || status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * The instruments sitting at one entity (FD-4482).
 *
 * `GET /legalEntity/:nodeType/:nodeId/instruments` — the other direction of the
 * capital-structure link. Each row names the company whose cap table it belongs to, so
 * the entity record can cross back into that company's consolidated stack.
 *
 * No ratchet on these rows: a ratchet is evaluated against a company's marks, and an
 * entity hosting instruments for two companies has no single set. The company-scoped read
 * decides it.
 */
export interface IHostedInstrument extends Omit<
  ICapitalStructureLayer,
  'rankGroup' | 'hostIsControl' | 'ratchet'
> {
  companyId: number | null;
}

export async function fetchHostedInstruments(
  nodeType: CapitalStructureHostNodeType,
  nodeId: number,
  params: ICapitalStructureQueryParams
): Promise<IHostedInstrument[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<IHostedInstrument[]>(
      `/legalEntity/${nodeType}/${nodeId}/instruments`,
      { params }
    );

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    // 403 belongs here too. Both instrument reads carry CAPTABLE_VIEW and a restricted-user
    // company check, so a refusal is an ordinary outcome for a caller without cap-table
    // access — rendering it as an error would tell the reader something had broken.
    if (status === 403 || status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * `EntityAppointment` — the governance seat model the D&O slate reads.
 *
 * Replaced `PersonCompanyLink`, and the difference is the point: an appointment hangs off
 * any node (a fund and a family can hold seats, a link keyed on a `companyRecord` cannot),
 * carries a lifecycle `status`, free-text `notes`, and the full compliance vocabulary
 * including the firm-held roles.
 */
export const APPOINTMENT_ROLES = [
  'DIRECTOR',
  'OFFICER',
  'MANAGING_MEMBER',
  'AUTHORIZED_SIGNATORY',
  'REGISTERED_AGENT',
  'OUTSIDE_COUNSEL',
] as const;

export type AppointmentRole = (typeof APPOINTMENT_ROLES)[number];

/** `pending` is a seat agreed but not yet effective — not a placeholder. */
export const APPOINTMENT_STATUSES = ['active', 'pending', 'resigned'] as const;

export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export interface IAppointment {
  id: number;
  personId: number;
  nodeType: LegalStructureNodeType;
  nodeId: number;
  /**
   * A plain string on read: the column takes the enum on write, but a read can carry a
   * value this build predates.
   */
  role: string;
  title: string | null;
  authorityScope: string | null;
  status: string;
  /** ISO date, or null when never recorded. */
  startDate: string | null;
  /** Null means the seat is open — the person still holds it. */
  endDate: string | null;
  notes: string | null;
}

export interface IAppointmentInput {
  personId: number;
  ref: ILegalStructureNodeRef;
  role: AppointmentRole;
  title?: string;
  authorityScope?: string;
  status?: AppointmentStatus;
  startDate?: string;
  endDate?: string;
  notes?: string;
}

/** The endpoint takes either a node or a person, and rejects neither. */
export async function fetchAppointmentsForNode(ref: ILegalStructureNodeRef): Promise<IAppointment[]> {
  const response = await MaggieHttp.maggie.get<IAppointment[]>(
    `/legalStructure/appointments?nodeType=${ref.nodeType}&nodeId=${ref.nodeId}`
  );

  return response.data;
}

/**
 * Every appointment in the tenant, for the slate's cross-entity views. Null on 404/422/501:
 * an API without the unfiltered read answers 422, which is not the same fact as a failure.
 */
export async function fetchAllAppointments(): Promise<IAppointment[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<IAppointment[]>('/legalStructure/appointments');

    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 404 || status === 422 || status === 501) {
      return null;
    }
    throw error;
  }
}

/**
 * Edges touching one node, in one direction: the endpoint filters a parent or a child, never
 * either end. Null when it is not deployed, matching the reads above.
 *
 * `asOf` pins the same way `/legalStructure/graph` does — edges are time-versioned BE-side,
 * so an unpinned read answers for today rather than for the date the caller is showing.
 * Optional because the registry's detail panel asks about an entity rather than about a
 * moment, and `EdgeQueryParams` defaults it.
 */
export async function fetchEntityRelationships(
  params:
    | { parentType: LegalStructureNodeType; parentId: number; asOf?: string }
    | { childType: LegalStructureNodeType; childId: number; asOf?: string }
): Promise<ILegalStructureEdge[] | null> {
  try {
    const response = await MaggieHttp.maggie.get<ILegalStructureEdge[]>('/entityRelationship', { params });

    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 501) {
      return null;
    }
    throw error;
  }
}

export async function createAppointment(input: IAppointmentInput): Promise<IAppointment> {
  const response = await MaggieHttp.maggie.post<IAppointmentInput, IAppointment>(
    '/legalStructure/appointments',
    input
  );

  return response.data;
}

/** Removes a mistaken seat entirely. A real appointment that closed is ended, not deleted. */
export async function deleteAppointment(id: number): Promise<void> {
  await MaggieHttp.maggie.delete(`/legalStructure/appointments/${id}`);
}

/** Partial by design: ending a seat sends a date and nothing else. */
export async function updateAppointment(
  id: number,
  input: Partial<Omit<IAppointmentInput, 'personId' | 'ref'>>
): Promise<IAppointment> {
  const response = await MaggieHttp.maggie.put<typeof input, IAppointment>(
    `/legalStructure/appointments/${id}`,
    input
  );

  return response.data;
}
